import React, { useEffect, useRef, useState } from 'react';
import { getTaskState } from '_services/remote-checkin-service';
import ReportIssueModal from './ReportIssueModal';
import {
    MdHourglassTop, MdPersonPin, MdDirectionsWalk, MdLocationOn,
    MdAccessible, MdCheckCircle, MdWarning, MdCancel, MdTimer, MdBlock,
} from 'react-icons/md';

// Polling interval 
// We do NOT use a direct Firestore listener here because the tasks collection
// requires authenticated access and passengers are unauthenticated.
// Instead we poll our own backend every 5 s — simple, reliable, no auth needed.
const POLL_INTERVAL_MS = 5000;

// State derivation 
// RC tasks now use the standard staff-app flow (acknowledged / isAtLocation /
// active / completed boolean fields).  We derive a passenger-facing display
// state from those booleans and fall back to task.state only for terminal
// conditions set by the backend (expired, no_show, cancelled) or the portal
// (allocated).
const deriveDisplayState = (data) => {
    if (!data) return 'created';
    if (data.completed) return 'completed';
    if (data.cancelledByController) return 'cancelled_by_controller';
    if (data.cancelledByPax) return 'cancelled_by_pax';
    if (data.cancelledBySystem) return 'cancelled_by_system';
    if (data.cancelled || data.state === 'cancelled') return 'cancelled';
    if (data.state === 'expired') return 'expired';
    if (data.state === 'no_show') return 'no_show';
    if (data.active) return 'started';
    if (data.isAtLocation) return 'at_location';   // agent arrived at pickup point
    if (data.acknowledged) return 'confirmed';       // agent confirmed, heading over
    if (data.allocatedCcaId || data.employeeId || data.state === 'allocated') return 'allocated';
    return 'created';
};

// Stepper
// 5-step progress bar.  'at_location' maps onto the same visual step as
// 'confirmed' so we don't add an extra dot for an internal operational step.
const STEPS = [
    { key: 'created', label: 'Received' },
    { key: 'allocated', label: 'Assigned' },
    { key: 'confirmed', label: 'On the way' },
    { key: 'started', label: 'Collected' },
    { key: 'completed', label: 'Done' },
];

const STEP_ORDER = STEPS.map(s => s.key);
const toStepKey = (derived) => derived === 'at_location' ? 'confirmed' : derived;

const TERMINAL = ['completed', 'no_show', 'cancelled', 'cancelled_by_controller', 'cancelled_by_pax', 'cancelled_by_system', 'expired'];

// Messages
const MESSAGE = {
    created: {
        Icon: MdHourglassTop,
        title: 'Request received',
        body: 'Your request has been sent. A team member will be assigned shortly.',
    },
    allocated: {
        Icon: MdPersonPin,
        title: 'Someone is on their way',
        body: 'A Customer Care Agent has been assigned and will come to collect you.',
    },
    confirmed: {
        Icon: MdDirectionsWalk,
        title: 'On their way to you',
        body: 'Your agent has confirmed and is heading to your location now.',
    },
    at_location: {
        Icon: MdLocationOn,
        title: 'Your agent has arrived',
        body: 'Your agent is at your location and will be with you in a moment.',
    },
    started: {
        Icon: MdAccessible,
        title: "You've been collected",
        body: 'Your agent is with you. They will escort you to the check-in area.',
    },
    completed: {
        Icon: MdCheckCircle,
        title: 'All done!',
        body: "You've been dropped off. Have a great flight!",
    },
    no_show: {
        Icon: MdWarning,
        title: 'Task closed',
        body: "Our agent couldn't locate you. Please contact a member of staff or call the number on the signage.",
    },
    cancelled: {
        Icon: MdCancel,
        title: 'Request cancelled',
        body: 'Your assistance request has been cancelled.',
    },
    cancelled_by_pax: {
        Icon: MdCancel,
        title: 'Request cancelled',
        body: 'You have cancelled your assistance request. Refresh the page to submit a new one.',
    },
    cancelled_by_controller: {
        Icon: MdBlock,
        title: 'Request cancelled',
        body: `Your assistance request has been cancelled by the controller.
        Please contact a member of staff if you still require assistance OR
        Refresh your screen to place a new assistance request`,
    },
    cancelled_by_system: {
        Icon: MdWarning,
        title: 'Request cancelled',
        body: 'Your assistance request has been cancelled. Please contact a member of staff or re-scan the QR code to try again.',
    },
    expired: {
        Icon: MdTimer,
        title: 'Request expired',
        body: 'Your request was not allocated in time. Please re-scan the QR code to try again.',
    },
};

// Stepper component 
const Stepper = ({ derived }) => {
    const stepKey = toStepKey(derived);
    const activeIdx = STEP_ORDER.indexOf(stepKey);
    return (
        <div className="rc-stepper" role="list" aria-label="Check-in progress">
            {STEPS.map((step, i) => {
                const isDone = i < activeIdx;
                const isActive = i === activeIdx;
                return (
                    <div key={step.key} className="rc-step" role="listitem">
                        <div
                            className={`rc-step-dot${isActive ? ' active' : isDone ? ' done' : ''}`}
                            aria-current={isActive ? 'step' : undefined}
                        >
                            {isDone ? '✓' : i + 1}
                        </div>
                        <span className={`rc-step-label${isActive ? ' active' : isDone ? ' done' : ''}`}>{step.label}</span>
                    </div>
                );
            })}
        </div>
    );
};

// StatusView
const StatusView = ({ initialState, onCancelled }) => {
    // Seed display state from the initialState prop (string from API on first load).
    // Subsequent updates come from the poll loop below.
    const [taskData, setTaskData] = useState(null);
    const [showReport, setShowReport] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        const poll = async () => {
            try {
                const data = await getTaskState();
                if (!data) return;
                setTaskData(data);

                // Once terminal, stop polling
                if (TERMINAL.includes(deriveDisplayState(data))) {
                    clearInterval(intervalRef.current);
                }
            } catch (err) {
                // Network hiccup — stay silent, retry next interval
                console.warn('[remotecheckin] poll error', err.message);
            }
        };

        // Fire immediately so the UI updates as fast as possible on mount
        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

        return () => clearInterval(intervalRef.current);
    }, []);

    // Use polled data when available; fall back to initialState seed
    const derived = taskData ? deriveDisplayState(taskData) : (initialState || 'created');
    const msg = MESSAGE[derived] || MESSAGE.created;
    const isTerminal = TERMINAL.includes(derived);
    const showStepper = !['no_show', 'cancelled', 'cancelled_by_pax', 'cancelled_by_controller', 'cancelled_by_system', 'expired'].includes(derived);
    // Show prominent report button once the agent has started or is at location
    const showReportBtn = derived === 'started' || derived === 'at_location';

    return (
        <div>
            {showStepper && <Stepper derived={derived} />}

            {derived === 'completed' ? (
                <div className="rc-completed-icon">
                    <span className="rc-completed-circle">✓</span>
                </div>
            ) : null}

            <div className="rc-status-message">
                {derived !== 'completed' && (
                    <span className="rc-status-icon" aria-hidden="true">
                        <msg.Icon size={40} />
                    </span>
                )}
                <p className="rc-status-title">{msg.title}</p>
                <p className="rc-status-body">{msg.body}</p>
            </div>

            {!isTerminal && (
                <p style={{ fontSize: '0.78rem', color: '#adb5bd', textAlign: 'center', marginTop: 8 }}>
                    This page updates automatically
                </p>
            )}

            {showReportBtn && (
                <button
                    type="button"
                    className="rc-btn-report"
                    onClick={() => setShowReport(true)}
                >
                    ⚠️ Report an issue
                </button>
            )}

            {!showReportBtn && !isTerminal && (
                <button
                    type="button"
                    className="rc-btn-secondary"
                    onClick={() => setShowReport(true)}
                >
                    Report an issue
                </button>
            )}

            {isTerminal && derived === 'expired' && (
                <button
                    type="button"
                    className="rc-btn-primary"
                    style={{ marginTop: 16 }}
                    onClick={onCancelled}
                >
                    Start new request
                </button>
            )}

            {isTerminal && derived !== 'completed' && (
                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6c757d', marginTop: 16 }}>
                    Need further help? Download the PRM Assist App
                </p>
            )}

            {showReport && (
                <ReportIssueModal
                    onClose={() => setShowReport(false)}
                    onCancelled={onCancelled}
                />
            )}
        </div>
    );
};

export default StatusView;
